import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { Observable, BehaviorSubject } from 'rxjs';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class WebSocketService implements OnDestroy {
  private socket!: Socket;

  // Remove trailing slash and keeps /api out of the socket endpoint.
  private readonly socketUrl = environment.apiUrl.replace(/\/api\/?$/, '').replace(/\/$/, '');

  readonly connected$ = new BehaviorSubject<boolean>(false);

  constructor(private zone: NgZone) {
    if (localStorage.getItem('access_token')) {
      this.connect();
    }
  }

  connect(): void {
    this.ensureSocket();
  }

  private ensureSocket(): Socket {
    if (this.socket) {
      if (!this.socket.connected && !this.socket.active) {
        this.socket.auth = { token: localStorage.getItem('access_token') };
        this.socket.connect();
      }
      return this.socket;
    }

    const token = localStorage.getItem('access_token');

    this.socket = io(this.socketUrl, {
      auth: { token },
      transports: ['polling', 'websocket'],
      upgrade: true,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      timeout: 20000,
    });

    this.socket.on('connect', () => {
      this.zone.run(() => {
        this.connected$.next(true);
      });
    });

    this.socket.io.engine.on('upgrade', (transport: any) => {
      console.log(`[WebSocket] Transporte melhorado para: ${transport.name}`);
    });

    this.socket.on('connect_error', (error) => {
      this.zone.run(() => {
        console.error('[WebSocket] Erro ao conectar:', error.message);
        this.connected$.next(false);
      });
    });

    this.socket.on('disconnect', (reason) => {
      this.zone.run(() => {
        console.warn('[WebSocket] Desconectado:', reason);
        this.connected$.next(false);
      });
    });

    this.socket.on('reconnect', (attempt: number) => {
      this.zone.run(() => {
        console.log(`[WebSocket] Reconectado apos ${attempt} tentativas`);
        this.connected$.next(true);
      });
    });

    return this.socket;
  }

  reconnectWithFreshToken(): void {
    if (this.socket) {
      this.socket.auth = { token: localStorage.getItem('access_token') };
      this.socket.disconnect().connect();
    } else {
      this.connect();
    }
  }

  private listenEvent<T = any>(eventName: string): Observable<T> {
    return new Observable((subscriber) => {
      const handler = (data: T) => {
        this.zone.run(() => subscriber.next(data));
      };

      const socket = this.ensureSocket();
      socket.on(eventName, handler);
      return () => socket.off(eventName, handler);
    });
  }

  listenTelemetry(deviceId?: number): Observable<any> {
    const eventName = deviceId ? `telemetry:${deviceId}` : 'telemetry:global';
    return this.listenEvent(eventName);
  }

  listenAlerts(): Observable<any> {
    return this.listenEvent('alert:global');
  }

  listenClusters(): Observable<any> {
    return this.listenEvent('cluster:global');
  }

  ngOnDestroy(): void {
    if (this.socket) {
      this.socket.disconnect();
    }
    this.connected$.complete();
  }
}
